import React, { useState, useEffect } from 'react';

import EventTracking from 'lib/EventTracking';

import BoxBody from 'components/BoxBody';
import { CancellationReasonsSelect } from 'coaching/components/CancellationReasonsSelect';
import FormElement from 'components/FormElement';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import Textarea from 'components/Textarea';

import { userReadableDateTimeFormatLocalTZ } from 'lib/dateTimeFormat';

type Props = {
  instanceUuid: string;
  instanceStartDateTime: string;
  wouldBeLateCancellation: boolean;
  isFreeTrial: boolean;
  eventPrefix?: string;
  onSelectCancellationReason?: (cancellationReason: string) => void;
  onChangeCancellationDetails?: (details?: string) => void;
};

export default function CancelCoachingSessionInstance({
  instanceUuid,
  instanceStartDateTime,
  wouldBeLateCancellation,
  isFreeTrial,
  eventPrefix,
  onSelectCancellationReason,
  onChangeCancellationDetails,
}: Props) {
  const [details, setDetails] = useState<string>('');

  function updateCancellationReason(value: string) {
    onSelectCancellationReason?.(value);
  }

  function updateCancellationDetails(value: string) {
    setDetails(value);
    onChangeCancellationDetails?.(value);
  }

  useEffect(() => {
    // reset state when the session instance changes
    updateCancellationReason('');
    updateCancellationDetails('');
  }, [instanceUuid]);

  function trackKey(key: string) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  const detailsLabel = (
    <Text size="xsmall">
      Tell us more <Text weight="normal">(Optional)</Text>
    </Text>
  );

  return (
    <>
      <BoxBody size="xsmall">
        <Row size="xsmall">
          <Text size="xsmall" weight="semibold">
            Session
          </Text>
        </Row>
        <Row size="small">
          <Text size="small">{userReadableDateTimeFormatLocalTZ(instanceStartDateTime)}</Text>
        </Row>
        <>
          {wouldBeLateCancellation && (
            <Row size="small">
              <LateCancellationNotice isFreeTrial={isFreeTrial} />
            </Row>
          )}
          <CancellationReasonsSelect
            sessionType="instance"
            changeState={(value: string) => updateCancellationReason(value)}
            eventPrefix={eventPrefix}
            userType="participant"
            instanceUuid={instanceUuid}
          />
          <div onFocus={_ => trackKey('tell_us_more.click')}>
            <FormElement
              data-test="cancellation-details-textarea"
              label={detailsLabel}
              element={Textarea}
              elementProps={{
                value: details,
                placeholder: 'Enter text',
              }}
              onValidChange={e => {
                updateCancellationDetails(e.target.value);
              }}
            />
          </div>
        </>
      </BoxBody>
    </>
  );
}

function LateCancellationNotice({ isFreeTrial }: { isFreeTrial: boolean }) {
  const noticeRows = isFreeTrial ? (
    <>
      <Row size="small">
        <Text>Your free trial session starts within 24 hours.</Text>
      </Row>
      <Row size="small">
        <Text>
          You will not be charged for cancelling this session, but you will be charged the full
          amount for your sessions going forward.
        </Text>
      </Row>
    </>
  ) : (
    <Row size="small">
      <Text>
        This session starts within 24 hours. You will be charged for the full session amount.
      </Text>
    </Row>
  );

  return (
    <BoxBody data-test="late-cancellation-notice" size="xsmall" dark rounded>
      {noticeRows}
      <Link weight="semibold" href={LinkConstants.COACHING_HELP_SCHEDULING} target="_blank">
        View our scheduling policy
      </Link>
    </BoxBody>
  );
}
